import React from "react";
import { Alert, AlertTitle, AlertDescription } from "@/components/ui/alert";
import { Sparkle } from "@phosphor-icons/react";
import SafeMarkdown from "./SafeMarkdown";
import CitationCard from "./CitationCard";

const REFUSAL_COPY = {
  insufficient_sources: {
    title: "No answer available",
    body:
      "The assistant could not find enough relevant sources in this workspace to answer confidently.",
    variant: "default",
  },
  citation_validation_failed: {
    title: "Answer withheld",
    body:
      "The assistant's citations could not be verified against the workspace sources. Please rephrase or ask a more specific question.",
    variant: "destructive",
  },
};

export default function AssistantAnswer({ turn }) {
  const { turnId, question, response } = turn;
  if (response.answered === true) {
    const dir = response.answer_language === "ar" ? "rtl" : "ltr";
    return (
      <div data-testid={`assistant-turn-${turnId}`} className="space-y-3">
        <div className="flex justify-end">
          <div data-testid="assistant-user-bubble" className="max-w-[85%] px-5 py-3 rounded-md text-sm leading-relaxed bg-[#0B1F3A] text-white">
            <div className="whitespace-pre-wrap">{question}</div>
          </div>
        </div>
        <div className="flex justify-start">
          <div
            data-testid="assistant-answer-bubble"
            dir={dir}
            lang={response.answer_language}
            className="max-w-[85%] px-5 py-3 rounded-md text-sm leading-relaxed bg-[#F4F6F8] text-[#0B1F3A] border border-black/5"
          >
            <div className="flex items-center gap-1.5 mb-1.5 text-xs font-mono uppercase tracking-widest text-[#0B4DCC]">
              <Sparkle size={12} weight="fill" /> AGENAIX
              {response.degraded === true && (
                <span
                  data-testid="assistant-degraded-pill"
                  className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-amber-50 text-amber-700 border border-amber-200"
                >
                  Limited retrieval mode
                </span>
              )}
            </div>
            <div className="prose prose-sm max-w-none">
              <SafeMarkdown>{response.answer}</SafeMarkdown>
            </div>
          </div>
        </div>
        {response.citations.length === 0 ? (
          <div className="text-xs text-neutral-500 italic pl-1">
            No sources attached to this answer
          </div>
        ) : (
          <div className="space-y-2">
            {response.citations.map((c, i) => (
              <CitationCard key={`${turnId}::${i}`} citation={c} />
            ))}
          </div>
        )}
      </div>
    );
  }
  const copy = REFUSAL_COPY[response.reason];
  return (
    <div data-testid={`assistant-turn-${turnId}`} className="space-y-3">
      <div className="flex justify-end">
        <div data-testid="assistant-user-bubble" className="max-w-[85%] px-5 py-3 rounded-md text-sm leading-relaxed bg-[#0B1F3A] text-white">
          <div className="whitespace-pre-wrap">{question}</div>
        </div>
      </div>
      <Alert variant={copy?.variant || "default"} data-testid="assistant-refusal-alert" dir="auto">
        <AlertTitle>{copy?.title}</AlertTitle>
        <AlertDescription>
          {copy?.body}
          {response.degraded === true && (
            <span
              data-testid="assistant-degraded-pill"
              className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-amber-50 text-amber-700 border border-amber-200"
            >
              Limited retrieval mode
            </span>
          )}
        </AlertDescription>
      </Alert>
    </div>
  );
}
